// Phase A.4 / 8A.5 — Premium Plan Card rendered inline in chat.
//
// Design Doc §5: Playfair Display goal names, gold border, scenario
// tabs (conservative / moderate / aggressive), week-by-week table
// with goal row highlighting, honest assessment, and action buttons.

import { useState } from "react";
import { Icon } from "../../../components/icons/Icon.tsx";

/* ── Types ───────────────────────────────────────────────────────── */

interface ScenarioRow {
  route?: string;
  targetAmount?: number;
  openingStake?: number;
  targetOdds?: number;
  takeoutRate?: number;
  estimatedWeeks?: number | null;
  fullChainProbability?: number;
  projectedFinalAmount?: number;
  protectedAmountAtBust?: number;
  downsideSummary?: string;
  feasible?: boolean;
  /** Week-by-week projections when available */
  weekProjections?: WeekRow[];
}

interface WeekRow {
  week: number;
  from: number;
  to: number;
  protected?: number;
  goalHit?: boolean;
}

interface ScenarioData {
  goalTitle?: string;
  scenarios?: ScenarioRow[];
  [key: string]: unknown;
}

/* ── Formatters ──────────────────────────────────────────────────── */

const fmtPct = (p: number): string => {
  const pct = p * 100;
  if (pct > 0 && pct < 0.1) return `${pct.toFixed(3)}%`;
  if (pct > 0 && pct < 1) return `${pct.toFixed(2)}%`;
  return `${pct.toFixed(1)}%`;
};
const fmtAmt = (n: number): string =>
  n.toLocaleString(undefined, { maximumFractionDigits: 0 });

const SCENARIO_LABELS: Record<string, string> = {
  conservative: "Conservative",
  moderate: "Moderate",
  aggressive: "Aggressive",
};

function guessScenarioLabel(route: string | undefined, index: number): string {
  if (!route) {
    const labels = ["Conservative", "Moderate", "Aggressive"];
    return labels[index] ?? `Route ${index + 1}`;
  }
  return SCENARIO_LABELS[route.toLowerCase()] ?? route;
}

/* ── Sub-components ──────────────────────────────────────────────── */

function WeekTable({ weeks }: { weeks: WeekRow[] }): JSX.Element {
  return (
    <div className="weeks-table">
      <div className="weeks-label">Week-by-week projection</div>
      {weeks.map((w) => (
        <div key={w.week} className={`wt-row${w.goalHit ? " goal-hit" : ""}`}>
          <span className="wkn">W{w.week}</span>
          <span className="from">{fmtAmt(w.from)}</span>
          <span className="arrow">→</span>
          <span className="to">{fmtAmt(w.to)}</span>
          {w.protected != null && (
            <span className="protected">{fmtAmt(w.protected)} protected</span>
          )}
          {w.goalHit && <span className="goal-tag">GOAL</span>}
        </div>
      ))}
    </div>
  );
}

function HonestAssessment({ scenario }: { scenario: ScenarioRow }): JSX.Element {
  return (
    <div className="honest-assessment">
      <div className="assess-label">Honest assessment</div>
      <div className="assess-row">
        <span className="lbl">Full chain probability</span>
        <span className="val">
          {fmtPct(scenario.fullChainProbability ?? 0)}
          <span className="prob-note">
            {(scenario.fullChainProbability ?? 0) < 0.15
              ? "(very difficult)"
              : (scenario.fullChainProbability ?? 0) < 0.35
                ? "(challenging)"
                : "(achievable)"}
          </span>
        </span>
      </div>
      <div className="assess-row">
        <span className="lbl">Projected final</span>
        <span className="val">{fmtAmt(scenario.projectedFinalAmount ?? 0)}</span>
      </div>
      <div className="assess-row">
        <span className="lbl">Protected at bust</span>
        <span className="val protected-val">
          {fmtAmt(scenario.protectedAmountAtBust ?? 0)}
        </span>
      </div>
    </div>
  );
}

/* ── Main Card ───────────────────────────────────────────────────── */

interface CardProps {
  data: unknown;
  /** Chat send function — when provided, action buttons are wired up. */
  onAction?: (text: string) => void;
  /** True while SIKA is still responding — hides action buttons. */
  sending?: boolean;
}

export function SikaScenarioInlineCard({ data, onAction, sending }: CardProps): JSX.Element {
  const [activeTab, setActiveTab] = useState(0);

  // Parse data into scenarios array
  let scenarios: ScenarioRow[] = [];
  let goalTitle = "Plan Comparison";

  if (data && typeof data === "object") {
    const d = data as ScenarioData;
    if (d.goalTitle) goalTitle = d.goalTitle;

    if (Array.isArray(d.scenarios)) {
      scenarios = d.scenarios;
    } else if (Array.isArray(data)) {
      scenarios = data as ScenarioRow[];
    } else if ("scenarios" in d && d.scenarios != null) {
      scenarios = d.scenarios as ScenarioRow[];
    } else {
      // Handle Record<route, scenario> shape
      scenarios = Object.values(data).filter(
        (v): v is ScenarioRow => typeof v === "object" && v !== null && "route" in (v as Record<string, unknown>),
      );
    }
  }

  if (scenarios.length === 0) {
    return (
      <div className="plan-card">
        <div className="plan-head">
          <div className="label">Scenarios</div>
          <div className="ttl">No scenarios available</div>
        </div>
      </div>
    );
  }

  const active = scenarios[activeTab] ?? scenarios[0];
  const first = scenarios[0];

  return (
    <div className="plan-card">
      {/* Header — Playfair Display */}
      <div className="plan-head">
        <div className="label">SIKA PLAN</div>
        <div className="ttl">{goalTitle}</div>
        {first?.targetAmount != null && (
          <span className="target-amount">
            GH₵ {fmtAmt(first.targetAmount)}
          </span>
        )}
        <div className="meta">
          {first?.openingStake != null
            ? `${fmtAmt(first.openingStake)} opening · `
            : ""}
          {scenarios.length} scenario{scenarios.length !== 1 ? "s" : ""} ·{" "}
          {first?.takeoutRate != null
            ? `${fmtPct(first.takeoutRate)} takeout`
            : "50% takeout default"}
        </div>
      </div>

      {/* Scenario tabs — only show if multiple */}
      {scenarios.length > 1 && (
        <div className="scenario-tabs">
          {scenarios.map((s, i) => (
            <button
              key={i}
              type="button"
              className={`scenario-tab${i === activeTab ? " active" : ""}`}
              onClick={() => setActiveTab(i)}
            >
              {guessScenarioLabel(s.route, i)}
            </button>
          ))}
        </div>
      )}

      {/* Stats summary */}
      {active && (
        <div className="plan-stats">
          <div className="stat-row">
            <span className="stat-label">Target odds</span>
            <span className="stat-value">{active.targetOdds?.toFixed(1) ?? "—"}x</span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Timeline</span>
            <span className="stat-value">
              {active.feasible !== false
                ? `${String(active.estimatedWeeks ?? "—")} weeks`
                : "Not reachable"}
            </span>
          </div>
          <div className="stat-row">
            <span className="stat-label">Chain probability</span>
            <span className="stat-value">{fmtPct(active.fullChainProbability ?? 0)}</span>
          </div>
        </div>
      )}

      {/* Week-by-week table */}
      {active?.weekProjections && active.weekProjections.length > 0 && (
        <WeekTable weeks={active.weekProjections} />
      )}

      {/* Honest assessment */}
      {active && <HonestAssessment scenario={active} />}

      {/* Downside summary */}
      {active?.downsideSummary && (
        <div className="plan-disclosure">{active.downsideSummary}</div>
      )}

      {/* Action buttons — hidden while SIKA is still responding */}
      {!sending && (
        <div className="plan-actions">
          <button
            type="button"
            className="btn-activate"
            onClick={() => {
              const label = active?.route ? guessScenarioLabel(active.route, activeTab) : "this";
              onAction?.(`Yes, activate the ${label.toLowerCase()} plan`);
            }}
          >
            <Icon name="bolt" size={14} />
            <span>ACTIVATE</span>
          </button>
          <button
            type="button"
            className="btn-adjust"
            onClick={() => onAction?.("I want to adjust the plan — can we look at different parameters?")}
          >
            ADJUST
          </button>
          <button
            type="button"
            className="btn-save"
            onClick={() => onAction?.("Save this plan for now, I'll decide later")}
          >
            SAVE
          </button>
        </div>
      )}

      {/* Disclosure */}
      <div className="plan-disclosure">
        Probabilities are mathematical estimates based on assumed odds. Actual outcomes depend on
        real match results. Past performance does not predict future results.
      </div>
    </div>
  );
}
